import { ClientConfig } from 'client/configuration';
import { featureFlags } from 'client/utils/feature-flags';
import * as sessionReplay from '@amplitude/session-replay-browser';
import { Identify, createInstance } from '@amplitude/analytics-browser';
import { isE2ETest } from 'site-modules/financing/utils/financing-utils';
import { requestContext } from 'client/utils/request-context/request-context';
import { logger } from 'client/utils/logger/logger';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { generateId } from './idGenerator';

const eventType = 'session_replay_link';

function getPageProps() {
  return {
    request_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    referrer: document.referrer || null,
    referrer_domain: document.referrer ? new URL(document.referrer).hostname : null,
  };
}

export async function initAmplitude(state) {
  const campaignName = 'plat-4390-v2';

  const isAmplitudeEnabled = featureFlags.get('amplitude');
  const clientIsBot = requestContext.get('loggerMetadata')?.isBot;
  const plat4390 = ExperimentUtil.getForcedOrAssignedRecipeName({
    state,
    campaignName,
    defaultVal: 'ctrl',
  });
  const isPlat4390Enabled = plat4390 === 'chal';

  ExperimentUtil.markRecipeUsed({ campaignName, usedRecipeName: plat4390 });

  if (isAmplitudeEnabled && isPlat4390Enabled && !isE2ETest(location) && !clientIsBot) {
    const { id: edwVisitorId, session: edwSessionId, isNewSession } = state.visitor;
    const { xApiKey } = ClientConfig.get('amplitude');

    try {
      if (isNewSession) {
        const hashedUserId = generateId(edwVisitorId).toString();
        const deviceId = hashedUserId;
        const amplitudeSessionId = Date.now();

        await sessionReplay.init(xApiKey, {
          deviceId,
          sessionId: amplitudeSessionId,
        }).promise;

        const amplitudeClient = createInstance();
        await amplitudeClient.init(xApiKey, hashedUserId, {
          deviceId,
          sessionId: amplitudeSessionId,
          defaultTracking: false,
        }).promise;

        const visitorSessionKey = `${hashedUserId}*${edwSessionId}`;

        amplitudeClient.setGroup('visitor_session_key', visitorSessionKey);

        const identifyEvent = new Identify();
        identifyEvent.set('visitor_session_key', visitorSessionKey);

        await amplitudeClient.identify(identifyEvent).promise;

        await amplitudeClient.track('session_start', getPageProps()).promise;
        const replayProps = sessionReplay.getSessionReplayProperties();

        if (replayProps && replayProps['[Amplitude] Session Replay ID']) {
          const result = await amplitudeClient.track(eventType, replayProps, {
            device_id: deviceId,
            session_id: amplitudeSessionId,
          }).promise;

          if (result.code !== 200) {
            const { code, message, body } = result;
            logger.warn({ code, message, details: body?.error }, 'Failed to send track event.');
          }
        } else {
          logger.info('Amplitude session was not sampled for replay. Skipping track event.');
        }
      }
    } catch (error) {
      logger.warn(error, 'Failed to initialize Amplitude or Session Replay');
    }
  }
}
